import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { HeroSection } from '@/components/HeroSection';
import { CategoryCard } from '@/components/CategoryCard';
import { QualifModal } from '@/components/QualifModal';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  categories, footerData, trackEvent,
  testimonials, faqItems, processSteps,
  captureTrafficSource,
} from '@/data/mock';
import {
  Leaf, ArrowRight, ShieldCheck, ChevronDown, ChevronUp,
  Quote, MapPin, CircleCheck,
} from 'lucide-react';

/* ── Lazy section (IntersectionObserver) ─────────────────── */
const LazySection = ({ children, className = '' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1, rootMargin: '50px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-[opacity,transform] duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      } ${className}`}
    >
      {children}
    </div>
  );
};

/* ── FAQ Accordion Item ──────────────────────────────── */
const FaqItem = ({ item, isOpen, onToggle }) => (
  <div
    data-testid={`faq-item-${item.question.slice(0, 20).replace(/\s/g, '-')}`}
    className="border border-gray-200 rounded-xl overflow-hidden transition-all duration-200 hover:border-teal-200"
  >
    <button
      onClick={onToggle}
      aria-expanded={isOpen}
      className="w-full min-h-[52px] flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50/50 transition-colors duration-200"
    >
      <span className="text-sm sm:text-base font-semibold text-[#1f2937] pr-4">{item.question}</span>
      {isOpen
        ? <ChevronUp className="h-5 w-5 text-[#0f766e] flex-shrink-0" aria-hidden="true" />
        : <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
      }
    </button>
    <div
      role="region"
      className={`overflow-hidden transition-all duration-300 ${
        isOpen ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'
      }`}
    >
      <p className="px-5 pb-5 text-sm text-gray-500 leading-relaxed">
        {item.answer}
      </p>
    </div>
  </div>
);

/* ── Main Page ───────────────────────────────────────── */
const LandingPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [showRGPD, setShowRGPD] = useState(
    () => !localStorage.getItem('rgpd_consent')
  );
  const rafIdRef = useRef(null);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    captureTrafficSource();
    trackEvent('page_view', { page: 'landing' });

    const handleScroll = () => {
      if (rafIdRef.current) return;
      rafIdRef.current = requestAnimationFrame(() => {
        setScrolled(window.scrollY > 20);
        rafIdRef.current = null;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setModalOpen(true);
    trackEvent('qualif_start', { category: category.id });
  };

  const scrollToCategories = () => {
    document
      .getElementById('categories')
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleRGPD = (accepted) => {
    localStorage.setItem('rgpd_consent', accepted ? 'accepted' : 'rejected');
    setShowRGPD(false);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans">
      {/* Skip to content (a11y) */}
      <a href="#categories" className="skip-link">
        Aller au contenu principal
      </a>

      {/* ── Top Disclaimer Banner (au-dessus du header) ──────────────── */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-50 border-b border-amber-300">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2 text-center">
          <p className="text-xs sm:text-sm text-amber-900 font-semibold">
            ⚠️ <strong>SERVICE PRIVÉ COMMERCIAL</strong> — Simulateur indépendant, non affilié à l'ANAH ni à maprimerenov.gouv.fr — Opéré par <strong>Danotec Service</strong>
          </p>
        </div>
      </div>

      {/* ── Header ───────────────────────────────────── */}
      <header
        data-testid="main-header"
        className={`fixed left-0 right-0 z-50 transition-all duration-300 top-[37px] ${
          scrolled
            ? 'bg-white/90 backdrop-blur-xl shadow-sm'
            : 'bg-white/60 backdrop-blur-sm'
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <Leaf className="h-6 w-6 text-[#0f766e]" />
            <div className="flex flex-col">
              <span className="text-lg font-bold text-[#1f2937] leading-tight">
                Aides Rénov Énergie
              </span>
              <span className="text-[10px] text-gray-400 leading-tight">
                Simulateur indépendant d'éligibilité MaPrimeRénov'
              </span>
            </div>
          </div>
          <Button
            data-testid="header-cta-btn"
            onClick={scrollToCategories}
            className="rounded-xl bg-[#0f766e] hover:bg-[#115e59] text-white text-sm h-9 px-4 shadow-sm transition-all duration-200 hover:shadow-md"
          >
            Vérifier éligibilité
          </Button>
        </div>
      </header>

      {/* ── Hero ─────────────────────────────────────── */}
      <HeroSection onQualify={scrollToCategories} />

      {/* ── Process Steps ────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-[#f8fafc]" aria-labelledby="process-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <LazySection>
            <div className="text-center mb-12">
              <h2 id="process-heading" className="text-2xl sm:text-3xl font-bold text-[#1f2937]">
                Comment ça marche ?
              </h2>
              <p className="mt-3 text-gray-500 max-w-xl mx-auto text-sm sm:text-base">
                4 étapes simples pour obtenir vos aides
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {processSteps.map((s, i) => (
                <div
                  key={i}
                  data-testid={`process-step-${i}`}
                  className="relative text-center group"
                >
                  {i < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-[60%] w-[80%] h-px bg-gradient-to-r from-teal-300 to-teal-100" aria-hidden="true" />
                  )}
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-teal-50 border-2 border-teal-100 mb-4 relative z-10 transition-all duration-300 group-hover:bg-teal-100 group-hover:scale-105">
                    <span className="text-xl font-extrabold text-[#0f766e]">{s.step}</span>
                  </div>
                  <h3 className="font-bold text-[#1f2937] mb-1.5 text-sm">{s.title}</h3>
                  <p className="text-xs text-gray-400 leading-relaxed max-w-[200px] mx-auto">{s.desc}</p>
                </div>
              ))}
            </div>
          </LazySection>
        </div>
      </section>

      {/* ── Categories ───────────────────────────────── */}
      <section id="categories" className="py-16 lg:py-24 bg-white" aria-labelledby="categories-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <LazySection>
            <div className="text-center mb-12">
              <h2 id="categories-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1f2937]">
                Choisissez votre projet
              </h2>
              <p className="mt-3 text-gray-500 max-w-xl mx-auto text-sm sm:text-base">
                Sélectionnez le type de travaux pour vérifier vos aides en
                2&nbsp;minutes
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {categories.map((cat) => (
                <CategoryCard
                  key={cat.id}
                  category={cat}
                  onSelect={handleCategorySelect}
                />
              ))}
            </div>
          </LazySection>
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────── */}
      <section data-testid="testimonials-section" className="py-16 lg:py-24 bg-[#f8fafc]" aria-labelledby="testimonials-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <LazySection>
            <div className="text-center mb-12">
              <h2 id="testimonials-heading" className="text-2xl sm:text-3xl font-bold text-[#1f2937]">
                Ils ont obtenu leurs aides
              </h2>
              <p className="mt-3 text-gray-500 max-w-xl mx-auto text-sm sm:text-base">
                Des milliers de foyers accompagnés avec succès
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {testimonials.map((t, i) => (
                <article
                  key={i}
                  data-testid={`testimonial-${i}`}
                  className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5"
                >
                  <div className="flex items-center gap-1 mb-4 text-amber-400" aria-label="5 étoiles sur 5">
                    <span className="text-base tracking-wide leading-none" aria-hidden="true">★★★★★</span>
                  </div>
                  <Quote className="h-5 w-5 text-teal-200 mb-3" aria-hidden="true" />
                  <blockquote className="text-sm text-gray-600 leading-relaxed mb-4">
                    {t.text}
                  </blockquote>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div>
                      <p className="text-sm font-semibold text-[#1f2937]">{t.name}</p>
                      <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                        <MapPin className="h-3 w-3" aria-hidden="true" />
                        {t.location} — {t.project}
                      </p>
                    </div>
                    <div className="bg-teal-50 rounded-lg px-2.5 py-1">
                      <span className="text-xs font-bold text-[#0f766e]">{t.score}%</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </LazySection>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────── */}
      <section id="faq" data-testid="faq-section" className="py-16 lg:py-24 bg-white" aria-labelledby="faq-heading">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <LazySection>
            <div className="text-center mb-12">
              <h2 id="faq-heading" className="text-2xl sm:text-3xl font-bold text-[#1f2937]">
                Questions fréquentes
              </h2>
              <p className="mt-3 text-gray-500 text-sm sm:text-base">
                Tout ce que vous devez savoir sur MaPrimeRénov' 2026
              </p>
            </div>

            <div className="space-y-3">
              {faqItems.map((item, i) => (
                <FaqItem
                  key={i}
                  item={item}
                  isOpen={openFaq === i}
                  onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
                />
              ))}
            </div>
          </LazySection>
        </div>
      </section>

      {/* ── CTA Band ─────────────────────────────────── */}
      <section id="cta" data-testid="cta-section" className="py-16 lg:py-20 bg-[#0f766e] relative overflow-hidden" aria-labelledby="cta-heading">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-[20%] w-64 h-64 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-0 right-[10%] w-96 h-96 rounded-full bg-teal-200 blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ShieldCheck className="h-12 w-12 text-teal-300 mx-auto mb-6" />
          <h2 id="cta-heading" className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Prêt à bénéficier de vos aides ?
          </h2>
          <p className="text-teal-100 text-base sm:text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
            Vérification gratuite, sans engagement. Un conseiller certifié RGE
            vous accompagne de A à Z dans vos démarches.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              data-testid="cta-main-btn"
              onClick={scrollToCategories}
              className="h-14 px-8 text-lg font-semibold rounded-xl shadow-lg bg-white text-[#0f766e] hover:bg-teal-50 transition-all duration-300 hover:scale-[1.02]"
            >
              Commencer maintenant
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2">
            {['Gratuit', 'Sans engagement', '2 minutes'].map((t, i) => (
              <span key={i} className="flex items-center gap-1.5 text-teal-200 text-sm">
                <CircleCheck className="h-4 w-4" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────── */}
      <footer data-testid="main-footer" className="bg-[#1f2937] text-gray-400 py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Leaf className="h-5 w-5 text-teal-400" />
                <span className="text-white font-bold text-lg">
                  Aides Rénov Énergie
                </span>
              </div>
              <p className="text-xs text-gray-400 mb-3">Simulateur indépendant d'éligibilité MaPrimeRénov'</p>
              <p className="text-sm leading-relaxed">
                Service privé d'accompagnement indépendant pour l'obtention des aides à la
                rénovation énergétique. Opéré par Danotec Service.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">
                Ressources
              </h4>
              <ul className="space-y-2.5">
                {footerData.resources.map((link) => (
                  <li key={link.label}>
                    {link.href.startsWith('/') ? (
                      <Link to={link.href} className="text-sm hover:text-teal-400 transition-colors duration-200">
                        {link.label}
                      </Link>
                    ) : (
                      <a href={link.href} className="text-sm hover:text-teal-400 transition-colors duration-200">
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">
                Informations légales
              </h4>
              <ul className="space-y-2.5">
                {footerData.legal.map((link) => (
                  <li key={link.label}>
                    <Link to={link.href} className="text-sm hover:text-teal-400 transition-colors duration-200">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <Separator className="bg-gray-700 mb-6" />

          <div className="text-xs text-gray-500 space-y-2">
            <p>
              MaPrimeRénov' est un dispositif d'aide à la rénovation
              énergétique géré par l'Agence Nationale de l'Habitat (ANAH).
              Ce site est un service privé indépendant opéré par Danotec Service — non affilié à l'ANAH ni au gouvernement.
            </p>
            <p>&copy; 2026 aides-renov-energie.com — Tous droits réservés. Site indépendant.</p>
            <p className="mt-1">Danotec Service — N° 515929636 (Registre des sociétés d'Israël)</p>
          </div>
        </div>
      </footer>

      {/* ── RGPD Banner ──────────────────────────────── */}
      {showRGPD && (
        <div
          data-testid="rgpd-banner"
          className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <p className="text-sm text-gray-600 flex-1 leading-relaxed">
              Ce site utilise des cookies pour améliorer votre expérience et
              mesurer l'audience.{' '}
              <a
                href="#confidentialite"
                className="text-[#0f766e] underline underline-offset-2"
                onClick={(e) => { e.preventDefault(); window.location.href = '/confidentialite'; }}
              >
                Politique de confidentialité
              </a>
            </p>
            <div className="flex gap-3 flex-shrink-0">
              <Button
                data-testid="rgpd-refuse-btn"
                onClick={() => handleRGPD(false)}
                variant="outline"
                className="rounded-xl text-sm h-9"
              >
                Refuser
              </Button>
              <Button
                data-testid="rgpd-accept-btn"
                onClick={() => handleRGPD(true)}
                className="rounded-xl bg-[#0f766e] hover:bg-[#115e59] text-white text-sm h-9 transition-colors duration-200"
              >
                Accepter
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Qualification Modal ──────────────────────── */}
      <QualifModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        category={selectedCategory}
      />
    </div>
  );
};

export default LandingPage;
